import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SiteContentProvider } from "./contexts/SiteContentContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import MediaPage from "./pages/MediaPage";
import AppsPage from "./pages/AppsPage";
import CoachingPage from "./pages/CoachingPage";
import MessagesPage from "./pages/MessagesPage";
import ProfilePage from "./pages/ProfilePage";
import SetUsernamePage from "./pages/SetUsernamePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AudioPage from "./pages/AudioPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminContent from "./pages/admin/AdminContent";
import AdminThemes from "./pages/admin/AdminThemes";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminSessions from "./pages/admin/AdminSessions";
import AdminSlots from "./pages/admin/AdminSlots";
import AdminApps from "./pages/admin/AdminApps";
import AdminPurchaseSettings from "./pages/admin/AdminPurchaseSettings";
import AdminAudio from "./pages/admin/AdminAudio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Callback Component
const AuthCallback = () => {
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (sessionId) {
      exchangeSession(sessionId)
        .then((data) => {
          if (data.needs_username) {
            window.location.href = '/set-username';
          } else {
            window.location.href = '/dashboard';
          }
        })
        .catch((error) => {
          console.error('Session exchange error:', error);
          window.location.href = '/login';
        });
    } else {
      window.location.href = '/login';
    }
  }, [location, exchangeSession]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background)' }}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--theme-primary)' }}></div>
        <p className="font-body" style={{ color: 'var(--theme-foreground)' }}>Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAuthenticated, isAdmin, needsUsername } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-background)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: 'var(--theme-primary)' }}></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsUsername && location.pathname !== '/set-username') {
    return <Navigate to="/set-username" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/media" element={<MediaPage />} />
      <Route path="/apps" element={<AppsPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/audio" element={<AudioPage />} />

      {/* Auth Routes */}
      <Route path="/set-username" element={
        <ProtectedRoute>
          <SetUsernamePage />
        </ProtectedRoute>
      } />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/coaching" element={
        <ProtectedRoute>
          <CoachingPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute adminOnly>
          <AdminContent />
        </ProtectedRoute>
      } />
      <Route path="/admin/themes" element={
        <ProtectedRoute adminOnly>
          <AdminThemes />
        </ProtectedRoute>
      } />
      <Route path="/admin/media" element={
        <ProtectedRoute adminOnly>
          <AdminMedia />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute adminOnly>
          <AdminMessages />
        </ProtectedRoute>
      } />
      <Route path="/admin/sessions" element={
        <ProtectedRoute adminOnly>
          <AdminSessions />
        </ProtectedRoute>
      } />
      <Route path="/admin/slots" element={
        <ProtectedRoute adminOnly>
          <AdminSlots />
        </ProtectedRoute>
      } />
      <Route path="/admin/apps" element={
        <ProtectedRoute adminOnly>
          <AdminApps />
        </ProtectedRoute>
      } />
      <Route path="/admin/purchase-settings" element={
        <ProtectedRoute adminOnly>
          <AdminPurchaseSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/audio" element={
        <ProtectedRoute adminOnly>
          <AdminAudio />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    // Track visit
    axios.post(`${API}/track-visit`).catch(() => {});
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SiteContentProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </SiteContentProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
